import React from 'react';
import { TutorialPage } from '../TutorialPage';
import { Share2, Trophy, ThumbsUp } from 'lucide-react';

export interface SharePageProps {
  currentPage: number;
  totalPages: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onFinish: () => void;
}

/**
 * Tutorial Page 5: Share & Leaderboard
 */
export const SharePage: React.FC<SharePageProps> = (props) => {
  return (
    <>
      {/* Dimmed backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" aria-hidden="true" />

      <TutorialPage
        title="Share & Leaderboard"
        position="center"
        cardClassName="!max-w-6xl"
        {...props}
      >
      <div className="space-y-4">
        {/* Main Content - Side by Side Layout */}
        <div className="mt-6 grid gap-6 md:grid-cols-3 md:items-center">
          {/* Features - Left Column */}
          <div className="space-y-4 md:space-y-5 md:self-center">
            {/* Share Results */}
            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Share2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">Share Your Results</h4>
                <p className="text-sm text-muted-foreground">
                  Show off your win streak and challenge friends to beat your score!
                </p>
              </div>
            </div>

            {/* Leaderboards */}
            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">Compete on Leaderboards</h4>
                <p className="text-sm text-muted-foreground">
                  See how you rank globally or create private groups with friends.
                </p>
              </div>
            </div>

            {/* Sentiment Voting */}
            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <ThumbsUp className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">Vote Your Sentiment</h4>
                <p className="text-sm text-muted-foreground">
                  Express your opinion: Buy, Hold, or Sell - and see what others think!
                </p>
              </div>
            </div>
          </div>

          {/* Results Example - Middle Column */}
          <div className="space-y-3 md:flex md:flex-col md:items-center md:justify-center">
            <p className="text-sm text-center text-muted-foreground font-semibold">
              Example results page
            </p>
            <div className="border-2 border-border rounded-lg overflow-hidden bg-white dark:bg-gray-900 w-full max-w-md mx-auto animate-in fade-in duration-500 delay-100">
              <img
                src="/assets/finished-modal-example.png"
                alt="Example of finished game modal showing results"
                className="w-full h-auto"
                loading="lazy"
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>

          {/* Leaderboard Example - Right Column */}
          <div className="space-y-3 md:flex md:flex-col md:items-center md:justify-center">
            <p className="text-sm text-center text-muted-foreground font-semibold">
              Example leaderboard
            </p>
            <div className="border-2 border-border rounded-lg overflow-hidden bg-white dark:bg-gray-900 w-full max-w-md mx-auto animate-in fade-in duration-500 delay-150">
              <img
                src="/assets/leaderboard-example.png"
                alt="Leaderboard screenshot highlighting player rankings"
                className="w-full h-auto"
                loading="lazy"
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </TutorialPage>
    </>
  );
};

export default SharePage;
